import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { FontIcon } from '@fluentui/react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import SConsciousApiService from '../services/SConsciousApiService';
import ActionButton from './ActionButton';

const api = new SConsciousApiService();

type Tab = 'overview' | 'analytics' | 'quality' | 'tables' | 'users' | 'notify' | 'activity' | 'prompts' | 'logs';

interface TableMeta { name: string }
interface UserRow {
  user_id: string; user_email: string; user_name: string; currency: string;
  created_at: string; status: 'active' | 'invited' | 'deleted'; receipts: number; items: number;
  receiptTotal: number; itemTotal: number; discrepancy: number;
  scons: number; isAdmin: boolean;
}

interface PromptSuggestionRow {
  prompt_suggestion_id: string;
  user_id: string;
  receipt_id: string;
  document_url: string;
  pipeline: string;
  issue: string;
  suggestion_count: number;
  suggestions: string[];
  created_at: string;
}

interface PromptGuidanceRow {
  prompt_guidance_id: string;
  guidance_text: string;
  priority: number;
  active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatValue(v: any): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + '…' : s;
}

/** Highlight matching substring in search results */
function highlightMatch(text: string, query: string): React.ReactNode {
  const t = truncate(text, 60);
  const idx = t.toLowerCase().indexOf(query);
  if (idx === -1) return <span>{t}</span>;
  return (
    <span>
      {t.slice(0, idx)}
      <mark style={{ background: 'rgba(255,204,0,0.35)', color: 'inherit', borderRadius: 2, padding: '0 1px' }}>{t.slice(idx, idx + query.length)}</mark>
      {t.slice(idx + query.length)}
    </span>
  );
}

// ── Chart palette ────────────────────────────────────────────────────────────
const CHART_COLORS = ['#ffcc00', '#e6b800', '#cc9900', '#b38600', '#997300', '#806000', '#664d00', '#4d3a00'];
const TOOLTIP_STYLE = { contentStyle: { background: '#111', border: '1px solid rgba(255,204,0,0.3)', borderRadius: 10, fontFamily: 'inherit', fontSize: '0.72rem' }, itemStyle: { color: '#fff' }, labelStyle: { color: '#ffcc00', fontWeight: 700 } } as const;

// ── Sub-components ───────────────────────────────────────────────────────────

/** Stat card used on the Overview tab */
function StatCard({ label, value, icon, onClick }: { label: string; value: number | string; icon: string; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'rgba(0,0,0,0.08)', borderRadius: 16, padding: '18px 20px',
        display: 'flex', flexDirection: 'column', gap: 4, minWidth: 130, flex: '1 1 130px',
        cursor: onClick ? 'pointer' : 'default', transition: 'background 0.15s',
      }}
      onMouseEnter={e => { if (onClick) (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,0.14)'; }}
      onMouseLeave={e => { if (onClick) (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,0.08)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FontIcon iconName={icon} style={{ fontSize: '1.1rem' }} />
        <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.6 }}>{label}</span>
        {onClick && <FontIcon iconName="ChevronRight" style={{ fontSize: '0.6rem', opacity: 0.35, marginLeft: 'auto' }} />}
      </div>
      <span style={{ fontSize: '1.6rem', fontWeight: 800 }}>{typeof value === 'number' ? value.toLocaleString() : value}</span>
    </div>
  );
}

/** Entity detail / JSON viewer & editor */
function EntityPanel({ entity, tableName, userId, onClose, onRefresh }: {
  entity: any; tableName: string; userId: string; onClose: () => void; onRefresh: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [json, setJson] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setJson(JSON.stringify(entity, null, 2));
    setEditing(false);
    setError('');
    setConfirmDelete(false);
  }, [entity]);

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const parsed = JSON.parse(json);
      const { partitionKey, rowKey, etag, timestamp, ...data } = parsed;
      const ok = await api.adminUpdateEntity(userId, tableName, entity.partitionKey, entity.rowKey, data);
      if (!ok) { setError('Update failed'); setSaving(false); return; }
      setEditing(false);
      onRefresh();
    } catch (e: any) {
      setError(e.message || 'Invalid JSON');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setSaving(true);
    const ok = await api.adminDeleteEntity(userId, tableName, entity.partitionKey, entity.rowKey);
    setSaving(false);
    if (ok) { onClose(); onRefresh(); }
    else setError('Delete failed');
  };

  return (
    <div style={{
      background: '#111', color: '#fff', borderRadius: 16, padding: '20px 18px',
      position: 'relative', maxHeight: '70vh', overflowY: 'auto',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#ffcc00' }}>
          {tableName} / {truncate(String(entity.rowKey), 20)}
        </span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '1.1rem', fontFamily: 'inherit' }}>✕</button>
      </div>
      {editing ? (
        <textarea
          value={json}
          onChange={e => setJson(e.target.value)}
          style={{
            width: '100%', minHeight: 300, background: 'rgba(255,255,255,0.06)', color: '#fff',
            border: '1px solid rgba(255,204,0,0.2)', borderRadius: 8, padding: 12,
            fontFamily: 'inherit', fontSize: '0.78rem', resize: 'vertical', boxSizing: 'border-box',
          }}
        />
      ) : (
        <pre style={{
          background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 14,
          fontSize: '0.72rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          maxHeight: '50vh', overflowY: 'auto', margin: 0, color: 'rgba(255,255,255,0.85)',
        }}>{json}</pre>
      )}
      {error && <p style={{ color: '#ff6b6b', fontSize: '0.78rem', margin: '8px 0 0' }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        {editing ? (
          <>
            <button onClick={handleSave} disabled={saving} style={{
              flex: 2, background: '#ffcc00', color: '#111', border: 'none', borderRadius: 12,
              padding: 11, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem',
            }}>{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={() => { setEditing(false); setJson(JSON.stringify(entity, null, 2)); setError(''); }} style={{
              flex: 1, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)',
              border: 'none', borderRadius: 12, padding: 11, cursor: 'pointer', fontFamily: 'inherit',
            }}>Cancel</button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)} style={{
              flex: 2, background: '#ffcc00', color: '#111', border: 'none', borderRadius: 12,
              padding: 11, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem',
            }}>Edit JSON</button>
            {confirmDelete ? (
              <button onClick={handleDelete} disabled={saving} style={{
                flex: 1, background: '#ff4444', color: '#fff', border: 'none', borderRadius: 12,
                padding: 11, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
              }}>{saving ? '…' : 'Confirm'}</button>
            ) : (
              <button onClick={() => setConfirmDelete(true)} style={{
                flex: 1, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)',
                border: 'none', borderRadius: 12, padding: 11, cursor: 'pointer', fontFamily: 'inherit',
              }}>Delete</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** User detail drill-down */
function UserDetail({ user, userId, onClose }: { user: UserRow; userId: string; onClose: () => void }) {
  const [tab, setTab] = useState<'receipts' | 'items'>('receipts');
  const [receipts, setReceipts] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.adminGetUserReceipts(userId, user.user_id),
      api.adminGetUserItems(userId, user.user_id),
    ]).then(([r, i]) => {
      setReceipts(r); setItems(i); setLoading(false);
    });
  }, [user.user_id, userId]);

  const data = tab === 'receipts' ? receipts : items;

  return (
    <div style={{
      background: '#111', color: '#fff', borderRadius: 16, padding: '20px 18px',
      maxHeight: '70vh', overflowY: 'auto',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <span style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#ffcc00' }}>
            {user.user_name || user.user_email || user.user_id}
          </span>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{user.user_email}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '1.1rem', fontFamily: 'inherit' }}>✕</button>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <StatCard label="Receipts" value={receipts.length} icon="Receipt" />
        <StatCard label="Items" value={items.length} icon="Product" />
        <StatCard label="Δ Total" value={`${user.discrepancy === 0 ? '0' : (user.discrepancy > 0 ? '+' : '') + user.discrepancy.toFixed(2)}`} icon="CalculatorEqualTo" />
        <StatCard label="sCoN's" value={user.scons} icon="FavoriteStar" />
        <StatCard label="AI Tokens" value={receipts.reduce((s: number, r: any) => s + (r.tokens_used ?? 0), 0).toLocaleString()} icon="Processing" />
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {(['receipts', 'items'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: tab === t ? '#ffcc00' : 'rgba(255,255,255,0.08)',
            color: tab === t ? '#111' : 'rgba(255,255,255,0.7)',
            border: 'none', borderRadius: 20, padding: '6px 16px', cursor: 'pointer',
            fontFamily: 'inherit', fontWeight: tab === t ? 700 : 400, fontSize: '0.78rem',
            textTransform: 'capitalize',
          }}>{t}</button>
        ))}
      </div>
      {loading ? <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>Loading…</p> : (
        <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
          {data.length === 0 ? <p style={{ color: 'rgba(255,255,255,0.35)', textAlign: 'center' }}>No {tab}</p> : (
            data.map((row: any, i: number) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 12px',
                marginBottom: 4, fontSize: '0.72rem', display: 'flex', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 4,
              }}>
                {tab === 'receipts' ? (
                  <>
                    <span style={{ fontWeight: 600 }}>{row.merchant_id ? truncate(row.merchant_id, 20) : row.rowKey}</span>
                    <span>{row.transaction_date || ''}</span>
                    <span style={{ color: '#ffcc00', fontWeight: 700 }}>{row.currency_code || ''} {Number(row.total_amount || 0).toFixed(2)}</span>
                  </>
                ) : (
                  <>
                    <span style={{ fontWeight: 600, flex: 1 }}>{truncate(row.description || row.rowKey || '', 30)}</span>
                    <span>{row.brand || ''}</span>
                    <span style={{ color: '#ffcc00', fontWeight: 700 }}>{Number(row.total_price || 0).toFixed(2)}</span>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Admin Portal ────────────────────────────────────────────────────────

export default function AdminPortal() {
  const userId = localStorage.getItem('signed_in_id') || '';
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [tab, setTab] = useState<Tab>('overview');

  // Overview
  const [stats, setStats] = useState<Record<string, number>>({});

  // Tables
  const [tables, setTables] = useState<TableMeta[]>([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [tableData, setTableData] = useState<any[]>([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [tablePage, setTablePage] = useState(0);
  const [tableFilter, setTableFilter] = useState('');
  const [tableFilterInput, setTableFilterInput] = useState('');
  const [tableLoading, setTableLoading] = useState(false);
  const PAGE_SIZE = 50;

  // Entity detail
  const [selectedEntity, setSelectedEntity] = useState<any | null>(null);

  // Users
  const [users, setUsers] = useState<UserRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [userSort, setUserSort] = useState<{ key: keyof UserRow; dir: 'asc' | 'desc' }>({ key: 'receipts', dir: 'desc' });

  // Analytics
  const [analytics, setAnalytics] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [tokenHealth, setTokenHealth] = useState<{ count: number; avg: number; p50: number; p90: number; p95: number; max: number; over20k: number; over30k: number; recent30Avg: number } | null>(null);

  // Data quality
  const [quality, setQuality] = useState<any>(null);
  const [qualityLoading, setQualityLoading] = useState(false);
  const qualityDetailRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollToDetail = (key: string) => {
    const el = qualityDetailRefs.current[key];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Unidentified brands
  const [unidentifiedBrands, setUnidentifiedBrands] = useState<any>(null);
  const [unidentifiedBrandsLoading, setUnidentifiedBrandsLoading] = useState(false);

  // Notify
  const [notifyTarget, setNotifyTarget] = useState('');
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyBody, setNotifyBody] = useState('');
  const [notifyBroadcast, setNotifyBroadcast] = useState(false);
  const [notifySending, setNotifySending] = useState(false);
  const [notifyResult, setNotifyResult] = useState<string | null>(null);

  // Activity feed
  const [activity, setActivity] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Prompt operations
  const [promptSuggestions, setPromptSuggestions] = useState<PromptSuggestionRow[]>([]);
  const [promptSuggestionsLoading, setPromptSuggestionsLoading] = useState(false);
  const [promptGuidance, setPromptGuidance] = useState<PromptGuidanceRow[]>([]);
  const [promptGuidanceLoading, setPromptGuidanceLoading] = useState(false);
  const [guidanceDraft, setGuidanceDraft] = useState('');
  const [guidancePriority, setGuidancePriority] = useState(100);
  const [promptPreview, setPromptPreview] = useState<{ active_count: number; active: any[]; preview_block: string } | null>(null);
  const [guidanceConflicts, setGuidanceConflicts] = useState<Array<{ prompt_guidance_id: string; guidance_text: string; reason: string }>>([]);
  const [promptResult, setPromptResult] = useState<string | null>(null);

  // Logs
  const [logs, setLogs] = useState<any[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsDate, setLogsDate] = useState(new Date().toISOString().slice(0, 10));
  const [logsLevel, setLogsLevel] = useState('');
  const [logsSearch, setLogsSearch] = useState('');
  const [logsPage, setLogsPage] = useState(0);
  const [logsExpanded, setLogsExpanded] = useState<string | null>(null);
  const LOGS_PAGE_SIZE = 100;

  // Global search
  const [globalQuery, setGlobalQuery] = useState('');
  const [globalResults, setGlobalResults] = useState<{ query: string; totalFound: number; results: { table: string; matches: any[] }[] } | null>(null);
  const [globalSearching, setGlobalSearching] = useState(false);
  const [globalOpen, setGlobalOpen] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Admin check
  useEffect(() => {
    if (!userId) { setIsAdmin(false); return; }
    api.adminCheck(userId).then(setIsAdmin);
  }, [userId]);

  // Global search — debounced
  const handleGlobalSearch = useCallback((q: string) => {
    setGlobalQuery(q);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (q.trim().length < 2) {
      setGlobalResults(null);
      setGlobalOpen(false);
      return;
    }
    setGlobalSearching(true);
    setGlobalOpen(true);
    searchTimerRef.current = setTimeout(() => {
      api.adminSearch(userId, q.trim()).then(r => {
        setGlobalResults(r);
        setGlobalSearching(false);
      });
    }, 400);
  }, [userId]);

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setGlobalOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load overview stats
  const loadStats = useCallback(() => {
    api.adminGetStats(userId).then(setStats);
  }, [userId]);

  // Load table list
  const loadTables = useCallback(() => {
    api.adminGetTables(userId).then(setTables);
  }, [userId]);

  // Load table data
  const loadTableData = useCallback((table: string, page: number, filter: string) => {
    if (!table) return;
    setTableLoading(true);
    api.adminGetTableData(userId, table, PAGE_SIZE, page * PAGE_SIZE, filter || undefined).then(result => {
      setTableData(result.data);
      setTableTotal(result.total);
      setTableLoading(false);
    });
  }, [userId]);

  // Load users
  const loadUsers = useCallback(() => {
    setUsersLoading(true);
    api.adminGetUsers(userId).then(u => { setUsers(u); setUsersLoading(false); });
  }, [userId]);

  // Load analytics
  const loadAnalytics = useCallback(() => {
    setAnalyticsLoading(true);
    api.adminGetAnalytics(userId).then(d => { setAnalytics(d); setAnalyticsLoading(false); });
    api.adminGetTokenHealth(userId).then(setTokenHealth);
  }, [userId]);

  // Load data quality
  const loadQuality = useCallback(() => {
    setQualityLoading(true);
    api.adminGetDataQuality(userId).then(d => { setQuality(d); setQualityLoading(false); });
  }, [userId]);

  // Load unidentified brands
  const loadUnidentifiedBrands = useCallback(() => {
    setUnidentifiedBrandsLoading(true);
    api.adminGetUnidentifiedBrands(userId).then(d => { setUnidentifiedBrands(d); setUnidentifiedBrandsLoading(false); });
  }, [userId]);

  // Load activity
  const loadActivity = useCallback(() => {
    setActivityLoading(true);
    api.adminGetActivity(userId, 100).then(d => { setActivity(d); setActivityLoading(false); });
  }, [userId]);

  // Load logs
  const loadLogs = useCallback((date: string, level: string, search: string, page: number) => {
    setLogsLoading(true);
    api.adminGetLogs(userId, {
      date: date || undefined,
      level: level || undefined,
      search: search || undefined,
      top: LOGS_PAGE_SIZE,
      skip: page * LOGS_PAGE_SIZE,
    }).then(r => {
      setLogs(r.data || []);
      setLogsTotal(r.total || 0);
      setLogsLoading(false);
    });
  }, [userId]);

  const loadPromptSuggestions = useCallback(() => {
    setPromptSuggestionsLoading(true);
    api.adminGetPromptSuggestions(userId, 100, 0).then((r) => {
      setPromptSuggestions(r.data || []);
      setPromptSuggestionsLoading(false);
    });
  }, [userId]);

  const loadPromptGuidance = useCallback(() => {
    setPromptGuidanceLoading(true);
    api.adminGetPromptGuidance(userId, true).then((rows) => {
      setPromptGuidance(rows || []);
      setPromptGuidanceLoading(false);
    });
    api.adminGetPromptGuidancePreview(userId).then((p) => setPromptPreview(p));
  }, [userId]);

  const addGuidance = useCallback(async (guidanceText: string, priority: number = 100) => {
    const txt = guidanceText.trim();
    if (!txt) return;
    const created = await api.adminAddPromptGuidance(userId, txt, priority, false);
    if ((created as any)?.conflict === true) {
      setGuidanceConflicts((created as any).conflicts || []);
      setPromptResult('Potential conflict detected. Review and click Add Anyway if intentional.');
      return;
    }
    if (created) {
      setPromptResult('Guidance saved and will be used in upcoming AI OCR runs.');
      setGuidanceDraft('');
      setGuidanceConflicts([]);
      loadPromptGuidance();
    } else {
      setPromptResult('Failed to add guidance.');
    }
  }, [userId, loadPromptGuidance]);

  const addGuidanceForced = useCallback(async () => {
    const txt = guidanceDraft.trim();
    if (!txt) return;
    const created = await api.adminAddPromptGuidance(userId, txt, guidancePriority, true);
    if (created) {
      setPromptResult('Guidance saved (forced) and will be used in upcoming AI OCR runs.');
      setGuidanceDraft('');
      setGuidanceConflicts([]);
      loadPromptGuidance();
    } else {
      setPromptResult('Failed to add guidance.');
    }
  }, [guidanceDraft, guidancePriority, userId, loadPromptGuidance]);

  const toggleGuidance = useCallback(async (row: PromptGuidanceRow, active: boolean) => {
    const ok = await api.adminSetPromptGuidanceActive(userId, row.prompt_guidance_id, active);
    if (ok) {
      setPromptResult(`Guidance ${active ? 'activated' : 'deactivated'}.`);
      loadPromptGuidance();
    } else {
      setPromptResult('Failed to update guidance.');
    }
  }, [userId, loadPromptGuidance]);

  const removeGuidance = useCallback(async (row: PromptGuidanceRow) => {
    const ok = await api.adminDeletePromptGuidance(userId, row.prompt_guidance_id);
    if (ok) {
      setPromptResult('Guidance removed.');
      loadPromptGuidance();
    } else {
      setPromptResult('Failed to remove guidance.');
    }
  }, [userId, loadPromptGuidance]);

  const setPriority = useCallback(async (row: PromptGuidanceRow, priority: number) => {
    const ok = await api.adminSetPromptGuidancePriority(userId, row.prompt_guidance_id, priority);
    if (ok) {
      setPromptResult('Guidance priority updated.');
      loadPromptGuidance();
    } else {
      setPromptResult('Failed to update priority.');
    }
  }, [userId, loadPromptGuidance]);

  // Send notification
  const handleSendNotify = async () => {
    if (!notifyTitle || !notifyBody) return;
    if (!notifyBroadcast && !notifyTarget) return;
    setNotifySending(true);
    setNotifyResult(null);
    const result = await api.adminSendNotification(userId, notifyBroadcast ? null : notifyTarget, notifyTitle, notifyBody, notifyBroadcast);
    setNotifySending(false);
    if (result) {
      setNotifyResult(`Sent to ${result.sent} device(s)`);
      setNotifyTitle('');
      setNotifyBody('');
      setNotifyTarget('');
    } else {
      setNotifyResult('Failed to send');
    }
  };

  // CSV export helper
  const exportTableData = (format: 'csv' | 'json') => {
    if (tableData.length === 0) return;
    let content: string;
    let mime: string;
    let ext: string;
    if (format === 'json') {
      content = JSON.stringify(tableData, null, 2);
      mime = 'application/json';
      ext = 'json';
    } else {
      const cols = tableColumns;
      const rows = tableData.map(row => cols.map(c => {
        const v = row[c];
        const s = v === null || v === undefined ? '' : String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','));
      content = [cols.join(','), ...rows].join('\n');
      mime = 'text/csv';
      ext = 'csv';
    }
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedTable}_export.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // On tab change, load required data
  useEffect(() => {
    if (!isAdmin) return;
    if (tab === 'overview') {
      loadStats();
      // Eagerly load data for mini widgets
      if (!analytics) loadAnalytics();
      if (!quality) loadQuality();
      if (activity.length === 0) loadActivity();
    }
    if (tab === 'tables') loadTables();
    if (tab === 'users') loadUsers();
    if (tab === 'analytics') loadAnalytics();
    if (tab === 'quality') { loadQuality(); loadUnidentifiedBrands(); }
    if (tab === 'activity') loadActivity();
    if (tab === 'prompts') { loadPromptSuggestions(); loadPromptGuidance(); }
    if (tab === 'logs') loadLogs(logsDate, logsLevel, logsSearch, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isAdmin]);

  // On table selection change
  useEffect(() => {
    if (selectedTable) {
      setTablePage(0);
      setTableFilter('');
      setTableFilterInput('');
      setSelectedEntity(null);
      loadTableData(selectedTable, 0, '');
    }
  }, [selectedTable, loadTableData]);

  // Columns to display for the selected table
  const tableColumns = useMemo(() => {
    if (tableData.length === 0) return [];
    const allKeys = new Set<string>();
    tableData.forEach(row => Object.keys(row).forEach(k => allKeys.add(k)));
    // prioritize useful keys, then sort the rest
    const priority = ['partitionKey', 'rowKey', 'user_id', 'user_email', 'user_name', 'name', 'description', 'merchant_id', 'receipt_id', 'total_amount', 'transaction_date', 'brand', 'status'];
    const ordered = priority.filter(k => allKeys.has(k));
    const rest = [...allKeys].filter(k => !priority.includes(k) && k !== 'etag' && k !== 'odata.etag').sort();
    return [...ordered, ...rest];
  }, [tableData]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    let list = users;
    if (userSearch) {
      const q = userSearch.toLowerCase();
      list = list.filter(u =>
        (u.user_email || '').toLowerCase().includes(q) ||
        (u.user_name || '').toLowerCase().includes(q) ||
        (u.user_id || '').toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a, b) => {
      const aVal = a[userSort.key];
      const bVal = b[userSort.key];
      if (typeof aVal === 'number' && typeof bVal === 'number') return userSort.dir === 'asc' ? aVal - bVal : bVal - aVal;
      return userSort.dir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
    return list;
  }, [users, userSearch, userSort]);

  const tokenHealthSeverity = useMemo(() => {
    const p90 = tokenHealth?.p90 ?? 0;
    if (!tokenHealth) return { label: 'Unknown', bg: 'rgba(0,0,0,0.08)', color: '#000' };
    if (p90 <= 18000) return { label: 'Healthy', bg: '#000', color: '#ffcc00' };
    if (p90 <= 25000) return { label: 'Watch', bg: '#e6b800', color: '#111' };
    return { label: 'High', bg: '#111', color: '#ffcc00' };
  }, [tokenHealth]);

  // Toggle admin status for a user
  const toggleAdmin = async (targetUser: UserRow) => {
    if (targetUser.isAdmin) {
      await api.adminRemoveAdmin(userId, targetUser.user_id);
    } else {
      await api.adminAddAdmin(userId, targetUser.user_id);
    }
    loadUsers();
  };

  // ── Cross-linking navigation helpers ──────────────────────────────────

  /** Jump to the Tables tab, select a table, and optionally pre-fill a filter */
  const navigateToTable = useCallback((tableName: string, filter?: string) => {
    setTab('tables');
    setSelectedEntity(null);
    setSelectedUser(null);
    // Pre-load table list so the dropdown shows the selection
    api.adminGetTables(userId).then(t => {
      setTables(t);
      setSelectedTable(tableName);
      if (filter) {
        setTableFilterInput(filter);
        setTableFilter(filter);
        setTablePage(0);
        loadTableData(tableName, 0, filter);
      }
    });
  }, [userId, loadTableData]);

  /** Jump to the Users tab and search for a specific user */
  const navigateToUser = useCallback((targetUserId: string) => {
    setTab('users');
    setSelectedEntity(null);
    setSelectedUser(null);
    setUserSearch(targetUserId);
    // Ensure users are loaded
    if (users.length === 0) {
      api.adminGetUsers(userId).then(u => {
        setUsers(u);
        setUsersLoading(false);
        const match = u.find((x: UserRow) => x.user_id === targetUserId);
        if (match) setSelectedUser(match);
      });
    } else {
      const match = users.find(u => u.user_id === targetUserId);
      if (match) setSelectedUser(match);
    }
  }, [userId, users]);

  // Table name mapping for stat card clicks
  const STAT_TABLE_MAP: Record<string, string> = {
    Users: 'Users', Receipts: 'Receipts', Items: 'Items',
    Merchants: 'Merchants', Brands: 'Brands', Corporations: 'Corporations',
    Friends: 'Friends', Chat: 'Chat', DeviceFcmTokens: 'DeviceFcmTokens',
    UserInteractions: 'UserInteractions', Admins: 'Admins',
  };

  // ── Render ──────────────────────────────────────────────────────────────

  if (isAdmin === null) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <span style={{ fontSize: '0.9rem', opacity: 0.6 }}>Checking permissions…</span>
    </div>;
  }

  if (!isAdmin) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 12 }}>
      <FontIcon iconName="Lock" style={{ fontSize: '2.5rem', opacity: 0.4 }} />
      <span style={{ fontSize: '0.9rem', opacity: 0.6 }}>Admin access required</span>
    </div>;
  }

  return (
    <div className="admin-portal">
      {/* Global Search Bar */}
      <div className="admin-search-container" ref={searchContainerRef}>
        <div className="admin-search-bar">
          <FontIcon iconName="Search" style={{ fontSize: '0.9rem', opacity: 0.4 }} />
          <input
            className="admin-search-input"
            placeholder="Search everywhere…"
            value={globalQuery}
            onChange={e => handleGlobalSearch(e.target.value)}
            onFocus={() => { if (globalResults && globalQuery.trim().length >= 2) setGlobalOpen(true); }}
          />
          {globalSearching && <span style={{ fontSize: '0.68rem', opacity: 0.4 }}>Searching…</span>}
          {globalQuery && (
            <button
              onClick={() => { setGlobalQuery(''); setGlobalResults(null); setGlobalOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.4, fontSize: '0.9rem', fontFamily: 'inherit', padding: '0 2px' }}
            >✕</button>
          )}
        </div>
        {/* Search results dropdown */}
        {globalOpen && globalResults && (
          <div className="admin-search-results">
            {globalResults.totalFound === 0 ? (
              <div style={{ padding: '16px 14px', textAlign: 'center', opacity: 0.5, fontSize: '0.78rem' }}>No results for "{globalResults.query}"</div>
            ) : (
              <>
                <div style={{ padding: '8px 14px 4px', fontSize: '0.68rem', opacity: 0.45, fontWeight: 600 }}>
                  {globalResults.totalFound} match{globalResults.totalFound !== 1 ? 'es' : ''} across {globalResults.results.length} table{globalResults.results.length !== 1 ? 's' : ''}
                </div>
                {globalResults.results.map(group => (
                  <div key={group.table}>
                    <div className="admin-search-group-header">
                      <FontIcon iconName="Table" style={{ fontSize: '0.72rem' }} />
                      <span>{group.table}</span>
                      <span style={{ opacity: 0.4, marginLeft: 'auto', fontSize: '0.65rem' }}>{group.matches.length}</span>
                    </div>
                    {group.matches.map((entity, i) => {
                      // Find which column(s) matched
                      const q = globalResults.query.toLowerCase();
                      const matchCols = Object.entries(entity).filter(([, v]) =>
                        v !== null && v !== undefined && String(v).toLowerCase().includes(q)
                      ).slice(0, 3);
                      return (
                        <div
                          key={i}
                          className="admin-search-result-row"
                          onClick={() => {
                            setGlobalOpen(false);
                            // Navigate to this entity in the table browser
                            navigateToTable(group.table);
                            // After a tick, select this entity so the detail panel opens
                            setTimeout(() => setSelectedEntity(entity), 200);
                          }}
                        >
                          {matchCols.map(([col, val]) => (
                            <span key={col} className="admin-search-match">
                              <span className="admin-search-match-col">{col}:</span>{' '}
                              {highlightMatch(String(val), q)}
                            </span>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Tab Bar */}
      <div className="admin-tabs">
        {(['overview', 'analytics', 'quality', 'tables', 'users', 'notify', 'activity', 'prompts', 'logs'] as Tab[]).map(t => (
          <button
            key={t}
            onClick={() => { setTab(t); setSelectedEntity(null); setSelectedUser(null); }}
            className={`admin-tab ${tab === t ? 'admin-tab--active' : ''}`}
          >{t}</button>
        ))}
      </div>

      {/* ── OVERVIEW (Command Center) ────────────────────────────────────── */}
      {tab === 'overview' && (
        <div className="admin-section">
          {/* Clickable stat cards — each navigates to its table */}
          <div className="admin-stats-grid">
            {Object.entries(STAT_TABLE_MAP).map(([key, table]) => {
              const icons: Record<string, string> = { Users: 'People', Receipts: 'Receipt', Items: 'Product', Merchants: 'Shop', Brands: 'Tag', Corporations: 'CityNext', Friends: 'AddFriend', Chat: 'Chat', DeviceFcmTokens: 'Ringer', UserInteractions: 'TouchPointer', Admins: 'Admin' };
              return (
                <StatCard
                  key={key}
                  label={key === 'DeviceFcmTokens' ? 'FCM Tokens' : key}
                  value={stats[key] ?? '…'}
                  icon={icons[key] || 'Info'}
                  onClick={() => key === 'Users' ? setTab('users') : navigateToTable(table)}
                />
              );
            })}
            {stats['TotalTokens'] != null && (
              <StatCard label="AI Tokens" value={stats['TotalTokens'].toLocaleString()} icon="Processing" />
            )}
          </div>

          {/* ── Two-column layout: mini chart + mini quality ── */}
          <div className="admin-overview-row">
            {/* Mini receipt chart */}
            <div className="admin-overview-card" onClick={() => setTab('analytics')} style={{ cursor: 'pointer' }}>
              <div className="admin-overview-card-header">
                <span className="admin-chart-title" style={{ margin: 0 }}>Receipts / Day</span>
                <FontIcon iconName="ChevronRight" style={{ fontSize: '0.65rem', opacity: 0.35 }} />
              </div>
              {analytics?.receiptsTimeline ? (
                <ResponsiveContainer width="100%" height={120}>
                  <LineChart data={analytics.receiptsTimeline.slice(-30)}>
                    <XAxis dataKey="date" hide />
                    <YAxis hide />
                    <Line type="monotone" dataKey="count" stroke="#000" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="total" stroke="#ffcc00" strokeWidth={1.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.3, fontSize: '0.78rem' }}>Loading…</div>
              )}
            </div>

            {/* Mini quality score */}
            <div className="admin-overview-card" onClick={() => setTab('quality')} style={{ cursor: 'pointer' }}>
              <div className="admin-overview-card-header">
                <span className="admin-chart-title" style={{ margin: 0 }}>Data Quality</span>
                <FontIcon iconName="ChevronRight" style={{ fontSize: '0.65rem', opacity: 0.35 }} />
              </div>
              {quality?.summary ? (() => {
                const total = quality.summary.totalReceipts + quality.summary.totalItems + quality.summary.totalMerchants;
                const issues = quality.summary.merchantsMissingCoords + quality.summary.itemsMissingBrand + quality.summary.itemsMissingProductType + quality.summary.receiptsZeroTotal + quality.summary.receiptsMissingDate + quality.summary.orphanedItems;
                const score = total > 0 ? Math.round(((total - issues) / total) * 100) : 100;
                return (
                  <div style={{ padding: '8px 0' }}>
                    <div style={{ textAlign: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: '2.2rem', fontWeight: 800 }}>{score}%</span>
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.12)', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                      <div style={{ background: score > 80 ? '#4caf50' : score > 50 ? '#ffcc00' : '#ff4444', height: '100%', width: `${score}%`, borderRadius: 6 }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: '0.68rem', opacity: 0.5 }}>
                      <span>{issues} issues</span>
                      <span>{total} entities</span>
                    </div>
                  </div>
                );
              })() : (
                <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.3, fontSize: '0.78rem' }}>Loading…</div>
              )}
            </div>
          </div>

          <div className="admin-overview-card" onClick={() => setTab('analytics')} style={{ cursor: 'pointer' }}>
            <div className="admin-overview-card-header">
              <span className="admin-chart-title" style={{ margin: 0 }}>AI Token Health</span>
              <span style={{
                fontSize: '0.64rem',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                background: tokenHealthSeverity.bg,
                color: tokenHealthSeverity.color,
                borderRadius: 20,
                padding: '4px 8px',
              }}>{tokenHealthSeverity.label}</span>
              <FontIcon iconName="ChevronRight" style={{ fontSize: '0.65rem', opacity: 0.35 }} />
            </div>
            {!tokenHealth ? (
              <div style={{ height: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.3, fontSize: '0.78rem' }}>Loading…</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(90px, 1fr))', gap: 8 }}>
                <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: '0.65rem', opacity: 0.5 }}>p50</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700 }}>{tokenHealth.p50.toLocaleString()}</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: '0.65rem', opacity: 0.5 }}>p90</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700 }}>{tokenHealth.p90.toLocaleString()}</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: '0.65rem', opacity: 0.5 }}>avg</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700 }}>{tokenHealth.avg.toLocaleString()}</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: '0.65rem', opacity: 0.5 }}>30d avg</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700 }}>{tokenHealth.recent30Avg.toLocaleString()}</div>
                </div>
              </div>
            )}
            {tokenHealth && (
              <div style={{ marginTop: 8, fontSize: '0.72rem', opacity: 0.65 }}>
                {tokenHealth.over20k} receipts over 20k tokens, {tokenHealth.over30k} over 30k, max {tokenHealth.max.toLocaleString()}.
              </div>
            )}
          </div>

          {/* ── Mini activity feed (last 8 events) ── */}
          <div className="admin-overview-card" onClick={() => setTab('activity')} style={{ cursor: 'pointer' }}>
            <div className="admin-overview-card-header">
              <span className="admin-chart-title" style={{ margin: 0 }}>Recent Activity</span>
              <FontIcon iconName="ChevronRight" style={{ fontSize: '0.65rem', opacity: 0.35 }} />
            </div>
            {activity.length > 0 ? (
              <div className="admin-activity-feed" style={{ gap: 2 }}>
                {activity.slice(0, 8).map((ev, i) => (
                  <div key={i} className="admin-activity-item" style={{ padding: '6px 10px' }}>
                    <div className="admin-activity-icon" style={{ width: 24, height: 24, fontSize: '0.72rem' }}>
                      <FontIcon iconName={
                        ev.type === 'receipt' ? 'Receipt' :
                        ev.type === 'registration' ? 'AddFriend' :
                        ev.type === 'interaction' ? 'Like' : 'Info'
                      } />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.72rem' }}>{truncate(ev.user || '—', 18)}</span>
                      <span style={{ fontSize: '0.68rem', opacity: 0.5, marginLeft: 6 }}>{truncate(ev.detail || '', 40)}</span>
                    </div>
                    <span className="admin-activity-badge" style={{ fontSize: '0.58rem' }}>{ev.type}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.3, fontSize: '0.78rem' }}>Loading…</div>
            )}
          </div>

          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <ActionButton label="Refresh All" onClick={() => { loadStats(); loadAnalytics(); loadQuality(); loadActivity(); }} icon={<FontIcon iconName="Refresh" />} />
          </div>
        </div>
      )}

      {/* ── TABLES ────────────────────────────────────────────────────────── */}
      {tab === 'tables' && (
        <div className="admin-section">
          {/* Table selector */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
            <select
              className="darkSelect"
              value={selectedTable}
              onChange={e => setSelectedTable(e.target.value)}
              style={{ maxWidth: 220 }}
            >
              <option value="">Select table…</option>
              {tables.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
            </select>
            {selectedTable && (
              <span style={{ fontSize: '0.72rem', opacity: 0.6 }}>{tableTotal} entities</span>
            )}
          </div>

          {/* Filter bar */}
          {selectedTable && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              <input
                className="darkInput"
                placeholder="OData filter e.g. user_id eq 'abc123'"
                value={tableFilterInput}
                onChange={e => setTableFilterInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { setTableFilter(tableFilterInput); setTablePage(0); loadTableData(selectedTable, 0, tableFilterInput); } }}
                style={{ flex: 1 }}
              />
              <ActionButton label="Filter" onClick={() => { setTableFilter(tableFilterInput); setTablePage(0); loadTableData(selectedTable, 0, tableFilterInput); }} />
              {tableFilter && <ActionButton label="Clear" onClick={() => { setTableFilterInput(''); setTableFilter(''); setTablePage(0); loadTableData(selectedTable, 0, ''); }} />}
              {tableData.length > 0 && (
                <>
                  <ActionButton label="CSV" onClick={() => exportTableData('csv')} icon={<FontIcon iconName="Download" />} />
                  <ActionButton label="JSON" onClick={() => exportTableData('json')} icon={<FontIcon iconName="Download" />} />
                </>
              )}
            </div>
          )}

          {/* Entity detail panel */}
          {selectedEntity && (
            <div style={{ marginBottom: 16 }}>
              <EntityPanel
                entity={selectedEntity}
                tableName={selectedTable}
                userId={userId}
                onClose={() => setSelectedEntity(null)}
                onRefresh={() => loadTableData(selectedTable, tablePage, tableFilter)}
              />
            </div>
          )}

          {/* Table grid */}
          {selectedTable && !tableLoading && tableData.length > 0 && (
            <div className="admin-table-scroll">
              <table className="admin-data-table">
                <thead>
                  <tr>
                    {tableColumns.map(col => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, i) => (
                    <tr key={i} onClick={() => setSelectedEntity(row)} style={{ cursor: 'pointer' }}>
                      {tableColumns.map(col => (
                        <td key={col}>{truncate(formatValue(row[col]), 40)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tableLoading && <p style={{ textAlign: 'center', opacity: 0.5, fontSize: '0.82rem' }}>Loading…</p>}

          {/* Pagination */}
          {selectedTable && tableTotal > PAGE_SIZE && (
            <div className="summary-table-pagination-container" style={{ marginTop: 10 }}>
              <span onClick={() => { if (tablePage > 0) { const p = tablePage - 1; setTablePage(p); loadTableData(selectedTable, p, tableFilter); } }}
                style={{ cursor: tablePage > 0 ? 'pointer' : 'default', opacity: tablePage > 0 ? 1 : 0.3 }}>
                <FontIcon iconName="ChevronLeft" />
              </span>
              <span style={{ fontSize: '0.78rem' }}>{tablePage * PAGE_SIZE + 1}–{Math.min((tablePage + 1) * PAGE_SIZE, tableTotal)} of {tableTotal}</span>
              <span onClick={() => { if ((tablePage + 1) * PAGE_SIZE < tableTotal) { const p = tablePage + 1; setTablePage(p); loadTableData(selectedTable, p, tableFilter); } }}
                style={{ cursor: (tablePage + 1) * PAGE_SIZE < tableTotal ? 'pointer' : 'default', opacity: (tablePage + 1) * PAGE_SIZE < tableTotal ? 1 : 0.3 }}>
                <FontIcon iconName="ChevronRight" />
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── USERS ─────────────────────────────────────────────────────────── */}
      {tab === 'users' && (
        <div className="admin-section">
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
            <input
              className="darkInput"
              placeholder="Search users…"
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              style={{ flex: 1, maxWidth: 300 }}
            />
            <span style={{ fontSize: '0.72rem', opacity: 0.6 }}>{filteredUsers.length} users</span>
            <ActionButton label="Refresh" onClick={loadUsers} icon={<FontIcon iconName="Refresh" />} />
          </div>

          {/* User detail panel */}
          {selectedUser && (
            <div style={{ marginBottom: 16 }}>
              <UserDetail user={selectedUser} userId={userId} onClose={() => setSelectedUser(null)} />
            </div>
          )}

          {usersLoading ? <p style={{ textAlign: 'center', opacity: 0.5 }}>Loading…</p> : (
            <div className="admin-table-scroll">
              <table className="admin-data-table">
                <thead>
                  <tr>
                    {(['user_name', 'user_email', 'currency', 'status', 'receipts', 'items', 'discrepancy', 'scons', 'isAdmin'] as (keyof UserRow)[]).map(col => (
                      <th key={col}
                        onClick={() => setUserSort(prev => ({ key: col, dir: prev.key === col && prev.dir === 'asc' ? 'desc' : 'asc' }))}
                        style={{ cursor: 'pointer' }}
                      >
                        {col === 'scons' ? "sCoN's" : col === 'discrepancy' ? 'Δ Total' : col.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}
                        {userSort.key === col && (userSort.dir === 'asc' ? ' ▲' : ' ▼')}
                      </th>
                    ))}
                    <th>actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => (
                    <tr key={u.user_id}>
                      <td onClick={() => setSelectedUser(u)} style={{ cursor: 'pointer', fontWeight: 600 }}>
                        {truncate(u.user_name || '—', 20)}
                      </td>
                      <td onClick={() => setSelectedUser(u)} style={{ cursor: 'pointer' }}>
                        {truncate(u.user_email || '—', 25)}
                      </td>
                      <td>{u.currency || '—'}</td>
                      <td style={{
                        fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em',
                        color: u.status === 'deleted' ? '#c00' : u.status === 'invited' ? '#b38600' : 'rgba(0,0,0,0.35)',
                      }}>{u.status}</td>
                      <td style={{ textAlign: 'right', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'rgba(0,0,0,0.2)' }}
                        onClick={() => navigateToTable('Receipts', `user_id eq '${u.user_id}'`)}>{u.receipts}</td>
                      <td style={{ textAlign: 'right', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'rgba(0,0,0,0.2)' }}
                        onClick={() => navigateToTable('Items', `user_id eq '${u.user_id}'`)}>{u.items}</td>
                      <td
                        style={{
                          textAlign: 'right',
                          fontWeight: Math.abs(u.discrepancy) > 1 ? 700 : 400,
                          color: u.discrepancy === 0 ? 'rgba(0,0,0,0.3)' : Math.abs(u.discrepancy) > 1 ? '#c00' : 'inherit',
                        }}
                        title={`Receipts: ${u.receiptTotal.toFixed(2)} | Items: ${u.itemTotal.toFixed(2)}`}
                      >{u.discrepancy === 0 ? '0' : u.discrepancy > 0 ? `+${u.discrepancy.toFixed(2)}` : u.discrepancy.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontWeight: u.scons > 0 ? 700 : 400, color: u.scons > 0 ? '#000' : 'rgba(0,0,0,0.3)' }}>{u.scons}</td>
                      <td style={{ textAlign: 'center' }}>
                        {u.isAdmin ? <span style={{ color: '#ffcc00', fontWeight: 700 }}>★</span> : '—'}
                      </td>
                      <td>
                        <button
                          onClick={() => toggleAdmin(u)}
                          style={{
                            background: u.isAdmin ? 'rgba(255,68,68,0.15)' : 'rgba(0,0,0,0.08)',
                            color: u.isAdmin ? '#ff4444' : 'inherit',
                            border: 'none', borderRadius: 8, padding: '4px 10px',
                            cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.72rem',
                          }}
                        >{u.isAdmin ? 'Revoke' : 'Grant'} Admin</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── ANALYTICS ─────────────────────────────────────────────────────── */}
      {tab === 'analytics' && (
        <div className="admin-section">
          {analyticsLoading || !analytics ? (
            <p style={{ textAlign: 'center', opacity: 0.5 }}>Loading analytics…</p>
          ) : (
            <>
              {/* Receipts per day — Line chart */}
              <div className="admin-chart-card">
                <span className="admin-chart-title">Receipts / Day (last 90 days)</span>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={analytics.receiptsTimeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: 'inherit' }} tickFormatter={(d: string) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10, fontFamily: 'inherit' }} />
                    <Tooltip {...TOOLTIP_STYLE} />
                    <Line type="monotone" dataKey="count" stroke="#000" strokeWidth={2} dot={false} name="Receipts" />
                    <Line type="monotone" dataKey="total" stroke="#ffcc00" strokeWidth={2} dot={false} name="Total Spend" />
                    <Legend wrapperStyle={{ fontSize: '0.72rem', fontFamily: 'inherit' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* User growth — Bar chart */}
              <div className="admin-chart-card">
                <span className="admin-chart-title">User Registrations / Month</span>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={analytics.usersTimeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: 'inherit' }} />
                    <YAxis tick={{ fontSize: 10, fontFamily: 'inherit' }} allowDecimals={false} />
                    <Tooltip {...TOOLTIP_STYLE} />
                    <Bar dataKey="count" fill="#000" radius={[6, 6, 0, 0]} name="Users" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="admin-chart-row">
                {/* Top merchants — Horizontal bar */}
                <div className="admin-chart-card" style={{ flex: 1 }}>
                  <span className="admin-chart-title">Top Merchants (by visits)</span>
                  <ResponsiveContainer width="100%" height={Math.max(220, (analytics.topMerchants?.length || 1) * 28)}>
                    <BarChart data={analytics.topMerchants} layout="vertical" margin={{ left: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                      <XAxis type="number" tick={{ fontSize: 10, fontFamily: 'inherit' }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fontFamily: 'inherit' }} width={80} />
                      <Tooltip {...TOOLTIP_STYLE} />
                      <Bar dataKey="count" fill="#ffcc00" radius={[0, 6, 6, 0]} name="Visits" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Top brands — Horizontal bar */}
                <div className="admin-chart-card" style={{ flex: 1 }}>
                  <span className="admin-chart-title">Top Brands (by items)</span>
                  <ResponsiveContainer width="100%" height={Math.max(220, (analytics.topBrands?.length || 1) * 28)}>
                    <BarChart data={analytics.topBrands} layout="vertical" margin={{ left: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                      <XAxis type="number" tick={{ fontSize: 10, fontFamily: 'inherit' }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fontFamily: 'inherit' }} width={80} />
                      <Tooltip {...TOOLTIP_STYLE} />
                      <Bar dataKey="count" fill="#000" radius={[0, 6, 6, 0]} name="Items" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Spending by currency — Pie */}
              <div className="admin-chart-card">
                <span className="admin-chart-title">Spending by Currency</span>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={analytics.spendByCurrency}
                      dataKey="total"
                      nameKey="currency"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ currency, total }: any) => `${currency} ${total.toFixed(0)}`}
                      labelLine={{ stroke: 'rgba(0,0,0,0.3)' }}
                      style={{ fontSize: '0.72rem', fontFamily: 'inherit' }}
                    >
                      {(analytics.spendByCurrency || []).map((_: any, i: number) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip {...TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <ActionButton label="Refresh" onClick={loadAnalytics} icon={<FontIcon iconName="Refresh" />} />
              </div>
            </>
          )}
        </div>
      )}

      {/* ── DATA QUALITY ──────────────────────────────────────────────────── */}
      {tab === 'quality' && (
        <div className="admin-section">
          {qualityLoading || !quality ? (
            <p style={{ textAlign: 'center', opacity: 0.5 }}>Analyzing data quality…</p>
          ) : (
            <>
              <div className="admin-stats-grid" style={{ marginBottom: 16 }}>
                <StatCard label="Missing Coords" value={quality.summary.merchantsMissingCoords} icon="MapPin" onClick={() => scrollToDetail('merchantsMissingCoords')} />
                <StatCard label="No Brand" value={quality.summary.itemsMissingBrand} icon="Tag" onClick={() => scrollToDetail('itemsMissingBrand')} />
                <StatCard label="No Product Type" value={quality.summary.itemsMissingProductType} icon="ProductCatalog" onClick={() => scrollToDetail('itemsMissingProductType')} />
                <StatCard label="Zero Total" value={quality.summary.receiptsZeroTotal} icon="Warning" onClick={() => scrollToDetail('receiptsZeroTotal')} />
                <StatCard label="No Date" value={quality.summary.receiptsMissingDate} icon="Calendar" onClick={() => scrollToDetail('receiptsMissingDate')} />
                <StatCard label="Orphaned Items" value={quality.summary.orphanedItems} icon="ErrorBadge" onClick={() => scrollToDetail('orphanedItems')} />
                <StatCard label="Unused Merchants" value={quality.summary.unusedMerchants} icon="StoreLogo16" onClick={() => scrollToDetail('unusedMerchants')} />
              </div>

              {/* Quality score bar */}
              {(() => {
                const total = quality.summary.totalReceipts + quality.summary.totalItems + quality.summary.totalMerchants;
                const issues = quality.summary.merchantsMissingCoords + quality.summary.itemsMissingBrand + quality.summary.itemsMissingProductType + quality.summary.receiptsZeroTotal + quality.summary.receiptsMissingDate + quality.summary.orphanedItems;
                const score = total > 0 ? Math.round(((total - issues) / total) * 100) : 100;
                return (
                  <div style={{ margin: '0 0 20px', background: 'rgba(0,0,0,0.08)', borderRadius: 12, padding: '14px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Data Quality Score</span>
                      <span style={{ fontSize: '1.2rem', fontWeight: 800 }}>{score}%</span>
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.12)', borderRadius: 6, height: 10, overflow: 'hidden' }}>
                      <div style={{ background: score > 80 ? '#4caf50' : score > 50 ? '#ffcc00' : '#ff4444', height: '100%', width: `${score}%`, borderRadius: 6, transition: 'width 0.5s' }} />
                    </div>
                  </div>
                );
              })()}

              {/* Detail: Merchants missing coords */}
              {quality.details.merchantsMissingCoords && quality.details.merchantsMissingCoords.length > 0 && (
                <div className="admin-quality-detail" ref={el => { qualityDetailRefs.current['merchantsMissingCoords'] = el; }}>
                  <span className="admin-chart-title">Merchants Missing Coordinates (top 50)</span>
                  <div className="admin-table-scroll" style={{ maxHeight: 250 }}>
                    <table className="admin-data-table">
                      <thead><tr><th>Merchant ID</th><th>Name</th><th>City</th></tr></thead>
                      <tbody>
                        {quality.details.merchantsMissingCoords.map((m: any, i: number) => (
                          <tr key={i} onClick={() => navigateToTable('Merchants', `merchant_id eq '${m.merchant_id}'`)} style={{ cursor: 'pointer' }}><td>{truncate(m.merchant_id, 30)}</td><td>{m.name}</td><td>{m.city}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Detail: Items missing brand */}
              {quality.details.itemsMissingBrand && quality.details.itemsMissingBrand.length > 0 && (
                <div className="admin-quality-detail" ref={el => { qualityDetailRefs.current['itemsMissingBrand'] = el; }}>
                  <span className="admin-chart-title">Items Missing Brand (top 50)</span>
                  <div className="admin-table-scroll" style={{ maxHeight: 250 }}>
                    <table className="admin-data-table">
                      <thead><tr><th>Item ID</th><th>Receipt ID</th><th>Description</th><th>User</th></tr></thead>
                      <tbody>
                        {quality.details.itemsMissingBrand.map((i: any, idx: number) => (
                          <tr key={idx} onClick={() => navigateToTable('Items', `item_id eq '${i.item_id}'`)} style={{ cursor: 'pointer' }}><td>{truncate(i.item_id, 24)}</td><td>{truncate(i.receipt_id, 20)}</td><td>{truncate(i.description, 30)}</td><td>{truncate(i.user_id, 16)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Detail: Items missing product type */}
              {quality.details.itemsMissingProductType && quality.details.itemsMissingProductType.length > 0 && (
                <div className="admin-quality-detail" ref={el => { qualityDetailRefs.current['itemsMissingProductType'] = el; }}>
                  <span className="admin-chart-title">Items Missing Product Type (top 50)</span>
                  <div className="admin-table-scroll" style={{ maxHeight: 250 }}>
                    <table className="admin-data-table">
                      <thead><tr><th>Item ID</th><th>Description</th><th>Brand</th><th>User</th></tr></thead>
                      <tbody>
                        {quality.details.itemsMissingProductType.map((i: any, idx: number) => (
                          <tr key={idx} onClick={() => navigateToTable('Items', `item_id eq '${i.item_id}'`)} style={{ cursor: 'pointer' }}><td>{truncate(i.item_id, 24)}</td><td>{truncate(i.description, 30)}</td><td>{i.brand}</td><td>{truncate(i.user_id, 16)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Detail: Receipts with zero total */}
              {quality.details.receiptsZeroTotal && quality.details.receiptsZeroTotal.length > 0 && (
                <div className="admin-quality-detail" ref={el => { qualityDetailRefs.current['receiptsZeroTotal'] = el; }}>
                  <span className="admin-chart-title">Receipts with Zero / Negative Total (top 50)</span>
                  <div className="admin-table-scroll" style={{ maxHeight: 250 }}>
                    <table className="admin-data-table">
                      <thead><tr><th>Receipt ID</th><th>User ID</th><th>Merchant ID</th><th>Total</th></tr></thead>
                      <tbody>
                        {quality.details.receiptsZeroTotal.map((r: any, i: number) => (
                          <tr key={i} onClick={() => navigateToTable('Receipts', `receipt_id eq '${r.receipt_id}'`)} style={{ cursor: 'pointer' }}><td>{truncate(r.receipt_id, 24)}</td><td>{truncate(r.user_id, 16)}</td><td>{truncate(r.merchant_id, 20)}</td><td>{r.total_amount}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Detail: Receipts missing date */}
              {quality.details.receiptsMissingDate && quality.details.receiptsMissingDate.length > 0 && (
                <div className="admin-quality-detail" ref={el => { qualityDetailRefs.current['receiptsMissingDate'] = el; }}>
                  <span className="admin-chart-title">Receipts Missing Date (top 50)</span>
                  <div className="admin-table-scroll" style={{ maxHeight: 250 }}>
                    <table className="admin-data-table">
                      <thead><tr><th>Receipt ID</th><th>User ID</th></tr></thead>
                      <tbody>
                        {quality.details.receiptsMissingDate.map((r: any, i: number) => (
                          <tr key={i} onClick={() => navigateToTable('Receipts', `receipt_id eq '${r.receipt_id}'`)} style={{ cursor: 'pointer' }}><td>{truncate(r.receipt_id, 30)}</td><td>{truncate(r.user_id, 20)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Detail: Orphaned items */}
              {quality.details.orphanedItems && quality.details.orphanedItems.length > 0 && (
                <div className="admin-quality-detail" ref={el => { qualityDetailRefs.current['orphanedItems'] = el; }}>
                  <span className="admin-chart-title">Orphaned Items (top 50)</span>
                  <div className="admin-table-scroll" style={{ maxHeight: 250 }}>
                    <table className="admin-data-table">
                      <thead><tr><th>Item ID</th><th>Receipt ID (missing)</th><th>Description</th><th>User</th></tr></thead>
                      <tbody>
                        {quality.details.orphanedItems.map((i: any, idx: number) => (
                          <tr key={idx} onClick={() => navigateToTable('Items', `item_id eq '${i.item_id}'`)} style={{ cursor: 'pointer' }}><td>{truncate(i.item_id, 24)}</td><td>{truncate(i.receipt_id, 20)}</td><td>{truncate(i.description, 30)}</td><td>{truncate(i.user_id, 16)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Detail: Unused merchants */}
              {quality.details.unusedMerchants && quality.details.unusedMerchants.length > 0 && (
                <div className="admin-quality-detail" ref={el => { qualityDetailRefs.current['unusedMerchants'] = el; }}>
                  <span className="admin-chart-title">Unused Merchants (top 50)</span>
                  <div className="admin-table-scroll" style={{ maxHeight: 250 }}>
                    <table className="admin-data-table">
                      <thead><tr><th>Merchant ID</th><th>Name</th><th>City</th></tr></thead>
                      <tbody>
                        {quality.details.unusedMerchants.map((m: any, i: number) => (
                          <tr key={i} onClick={() => navigateToTable('Merchants', `merchant_id eq '${m.merchant_id}'`)} style={{ cursor: 'pointer' }}><td>{truncate(m.merchant_id, 30)}</td><td>{m.name}</td><td>{m.city}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Detail: Unidentified brands by store / product */}
              {unidentifiedBrands && unidentifiedBrands.rows && unidentifiedBrands.rows.length > 0 && (
                <div className="admin-quality-detail" ref={el => { qualityDetailRefs.current['unidentifiedBrands'] = el; }}>
                  <span className="admin-chart-title">Unidentified Brands by Store &amp; Product ({unidentifiedBrands.total} occurrences, {unidentifiedBrands.uniqueProducts} unique)</span>
                  {unidentifiedBrandsLoading ? (
                    <p style={{ textAlign: 'center', opacity: 0.5 }}>Loading…</p>
                  ) : (
                    <div className="admin-table-scroll" style={{ maxHeight: 320 }}>
                      <table className="admin-data-table">
                        <thead><tr><th>Store</th><th>Product</th><th>Count</th><th>Users</th><th>Last Seen</th></tr></thead>
                        <tbody>
                          {unidentifiedBrands.rows.map((r: any, i: number) => (
                            <tr key={i} onClick={() => navigateToTable('UnidentifiedBrands', `merchant_name eq '${r.merchant_name}'`)} style={{ cursor: 'pointer' }}>
                              <td>{r.merchant_name}</td>
                              <td title={r.product_identifier}>{truncate(r.description, 30)}</td>
                              <td>{r.count}</td>
                              <td>{r.users?.length ?? 0}</td>
                              <td>{r.last_seen ? new Date(r.last_seen).toLocaleDateString() : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <ActionButton label="Refresh" onClick={loadQuality} icon={<FontIcon iconName="Refresh" />} />
              </div>
            </>
          )}
        </div>
      )}

      {/* ── PUSH NOTIFICATIONS ────────────────────────────────────────────── */}
      {tab === 'notify' && (
        <div className="admin-section">
          <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 16, padding: '20px 18px', maxWidth: 500, margin: '0 auto' }}>
            <span className="admin-chart-title" style={{ marginBottom: 14, display: 'block' }}>Send Push Notification</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={notifyBroadcast}
                  onChange={e => { setNotifyBroadcast(e.target.checked); if (e.target.checked) setNotifyTarget(''); }}
                  style={{ accentColor: '#ffcc00' }}
                />
                Broadcast to all users
              </label>
              {!notifyBroadcast && (
                <input
                  className="darkInput"
                  placeholder="Target user ID"
                  value={notifyTarget}
                  onChange={e => setNotifyTarget(e.target.value)}
                />
              )}
              <input
                className="darkInput"
                placeholder="Notification title"
                value={notifyTitle}
                onChange={e => setNotifyTitle(e.target.value)}
              />
              <textarea
                className="darkInput"
                placeholder="Notification body"
                value={notifyBody}
                onChange={e => setNotifyBody(e.target.value)}
                rows={3}
                style={{ resize: 'vertical', fontFamily: 'inherit' }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleSendNotify}
                  disabled={notifySending || !notifyTitle || !notifyBody || (!notifyBroadcast && !notifyTarget)}
                  style={{
                    flex: 1, background: notifyBroadcast ? '#ff4444' : '#ffcc00',
                    color: notifyBroadcast ? '#fff' : '#111',
                    border: 'none', borderRadius: 12, padding: 12, fontWeight: 800,
                    cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem',
                    opacity: (notifySending || !notifyTitle || !notifyBody) ? 0.5 : 1,
                  }}
                >{notifySending ? 'Sending…' : notifyBroadcast ? 'Broadcast' : 'Send'}</button>
              </div>
              {notifyResult && (
                <p style={{ fontSize: '0.78rem', textAlign: 'center', fontWeight: 600, margin: 0,
                  color: notifyResult.startsWith('Failed') ? '#ff4444' : '#000'
                }}>{notifyResult}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── ACTIVITY FEED ─────────────────────────────────────────────────── */}
      {tab === 'activity' && (
        <div className="admin-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: '0.72rem', opacity: 0.6 }}>{activity.length} events</span>
            <ActionButton label="Refresh" onClick={loadActivity} icon={<FontIcon iconName="Refresh" />} />
          </div>
          {activityLoading ? (
            <p style={{ textAlign: 'center', opacity: 0.5 }}>Loading activity…</p>
          ) : (
            <div className="admin-activity-feed">
              {activity.map((ev, i) => (
                <div key={i} className="admin-activity-item">
                  <div className="admin-activity-icon">
                    <FontIcon iconName={
                      ev.type === 'receipt' ? 'Receipt' :
                      ev.type === 'registration' ? 'AddFriend' :
                      ev.type === 'interaction' ? 'Like' : 'Info'
                    } />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span
                        style={{ fontWeight: 600, fontSize: '0.78rem', cursor: ev.userId ? 'pointer' : 'default', textDecoration: ev.userId ? 'underline' : 'none', textDecorationColor: 'rgba(0,0,0,0.2)', textUnderlineOffset: '2px' }}
                        onClick={e => { if (ev.userId) { e.stopPropagation(); navigateToUser(ev.userId); } }}
                      >{truncate(ev.user || '—', 24)}</span>
                      <span className="admin-activity-badge">{ev.type}</span>
                    </div>
                    <div style={{ fontSize: '0.72rem', opacity: 0.6, marginTop: 2 }}>{truncate(ev.detail || '', 60)}</div>
                  </div>
                  <span style={{ fontSize: '0.65rem', opacity: 0.4, whiteSpace: 'nowrap', marginLeft: 8 }}>
                    {ev.timestamp ? new Date(ev.timestamp).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
              {activity.length === 0 && <p style={{ textAlign: 'center', opacity: 0.4, fontSize: '0.82rem' }}>No activity yet</p>}
            </div>
          )}
        </div>
      )}

      {/* ── PROMPT TRAINING ─────────────────────────────────────────────── */}
      {tab === 'prompts' && (
        <div className="admin-section">
          <div style={{ display: 'grid', gap: 14, gridTemplateColumns: '1fr', marginBottom: 14 }}>
            <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 16, padding: '14px 14px' }}>
              <span className="admin-chart-title" style={{ marginBottom: 8, display: 'block' }}>Add Training Guidance</span>
              <p style={{ margin: '0 0 8px', fontSize: '0.76rem', opacity: 0.6 }}>
                These lines are injected into AI OCR prompts so the model takes them into account on future scans.
              </p>
              <textarea
                className="darkInput"
                value={guidanceDraft}
                onChange={e => { setGuidanceDraft(e.target.value); setGuidanceConflicts([]); }}
                onBlur={async () => {
                  const txt = guidanceDraft.trim();
                  if (!txt) { setGuidanceConflicts([]); return; }
                  const check = await api.adminValidatePromptGuidance(userId, txt);
                  setGuidanceConflicts(check?.conflicts || []);
                }}
                rows={3}
                placeholder="Example: If a line contains STATIEGELD or LEEGGOED, classify it as product_type Deposit and merge split amount lines."
                style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: '0.74rem', opacity: 0.65 }}>Priority</span>
                <input
                  className="darkInput"
                  type="number"
                  min={1}
                  max={999}
                  value={guidancePriority}
                  onChange={e => setGuidancePriority(Math.max(1, Math.min(999, Number(e.target.value) || 100)))}
                  style={{ width: 100 }}
                />
                <span style={{ fontSize: '0.72rem', opacity: 0.5 }}>lower runs first</span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <ActionButton label="Add Guidance" onClick={() => addGuidance(guidanceDraft, guidancePriority)} icon={<FontIcon iconName="Add" />} />
                {guidanceConflicts.length > 0 && (
                  <ActionButton label="Add Anyway" onClick={addGuidanceForced} icon={<FontIcon iconName="Warning" />} />
                )}
                <ActionButton label="Refresh" onClick={() => { loadPromptSuggestions(); loadPromptGuidance(); }} icon={<FontIcon iconName="Refresh" />} />
              </div>
              {guidanceConflicts.length > 0 && (
                <div style={{ marginTop: 10, background: 'rgba(0,0,0,0.08)', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: '0.74rem', fontWeight: 700, marginBottom: 6 }}>Potential conflicts:</div>
                  {guidanceConflicts.slice(0, 4).map((c, idx) => (
                    <div key={idx} style={{ fontSize: '0.72rem', opacity: 0.8, marginBottom: 4 }}>
                      • {c.reason} Existing: {truncate(c.guidance_text || '', 120)}
                    </div>
                  ))}
                </div>
              )}
              {promptResult && <p style={{ margin: '8px 0 0', fontSize: '0.75rem', opacity: 0.7 }}>{promptResult}</p>}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <span className="admin-chart-title" style={{ marginBottom: 8, display: 'block' }}>Effective Prompt Preview</span>
            <div style={{ background: 'rgba(0,0,0,0.06)', borderRadius: 12, padding: '10px 12px' }}>
              <div style={{ fontSize: '0.72rem', opacity: 0.6, marginBottom: 6 }}>
                Active guidance lines: {promptPreview?.active_count ?? 0}
              </div>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.72rem', opacity: 0.9 }}>
                {promptPreview?.preview_block || '(no active guidance)'}
              </pre>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <span className="admin-chart-title" style={{ marginBottom: 8, display: 'block' }}>Active / Saved Guidance</span>
            {promptGuidanceLoading ? <p style={{ opacity: 0.5 }}>Loading guidance…</p> : (
              <div className="admin-table-scroll">
                <table className="admin-data-table">
                  <thead>
                    <tr><th>Status</th><th>Priority</th><th>Guidance</th><th>Updated</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {promptGuidance.map((g) => (
                      <tr key={g.prompt_guidance_id}>
                        <td style={{ fontWeight: 700 }}>{g.active ? 'Active' : 'Inactive'}</td>
                        <td style={{ minWidth: 90 }}>{Number(g.priority || 100)}</td>
                        <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 480, lineHeight: 1.45 }}>{g.guidance_text || ''}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{g.updated_at ? new Date(g.updated_at).toLocaleString() : '—'}</td>
                        <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button
                            onClick={() => toggleGuidance(g, !g.active)}
                            style={{ background: 'rgba(0,0,0,0.12)', color: '#000', border: 'none', borderRadius: 8, padding: '4px 8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.72rem' }}
                          >{g.active ? 'Deactivate' : 'Activate'}</button>
                          <button
                            onClick={() => setPriority(g, Math.max(1, Number(g.priority || 100) - 1))}
                            style={{ background: 'rgba(0,0,0,0.12)', color: '#000', border: 'none', borderRadius: 8, padding: '4px 8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.72rem' }}
                          >Higher</button>
                          <button
                            onClick={() => setPriority(g, Math.min(999, Number(g.priority || 100) + 1))}
                            style={{ background: 'rgba(0,0,0,0.12)', color: '#000', border: 'none', borderRadius: 8, padding: '4px 8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.72rem' }}
                          >Lower</button>
                          <button
                            onClick={() => removeGuidance(g)}
                            style={{ background: 'rgba(0,0,0,0.12)', color: '#000', border: 'none', borderRadius: 8, padding: '4px 8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.72rem' }}
                          >Delete</button>
                        </td>
                      </tr>
                    ))}
                    {promptGuidance.length === 0 && (
                      <tr><td colSpan={5} style={{ opacity: 0.5, textAlign: 'center' }}>No guidance saved yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <span className="admin-chart-title" style={{ marginBottom: 8, display: 'block' }}>Model Suggestions From Anomalies</span>
            {promptSuggestionsLoading ? <p style={{ opacity: 0.5 }}>Loading suggestions…</p> : (
              <div className="admin-table-scroll">
                <table className="admin-data-table">
                  <thead>
                    <tr><th>When</th><th>Issue</th><th>Suggestions</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {promptSuggestions.map((s) => (
                      <tr key={s.prompt_suggestion_id}>
                        <td>{s.created_at ? new Date(s.created_at).toLocaleString() : '—'}</td>
                        <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 320, lineHeight: 1.45 }}>{s.issue || ''}</td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {(s.suggestions || []).map((line, idx) => (
                              <button
                                key={idx}
                                onClick={() => addGuidance(line, 100)}
                                style={{ textAlign: 'left', background: 'rgba(0,0,0,0.08)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.72rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 }}
                                title="Click to add this as active guidance"
                              >+ {line}</button>
                            ))}
                          </div>
                        </td>
                        <td>
                          <ActionButton label="Apply All" onClick={async () => {
                            for (const line of (s.suggestions || [])) {
                              // eslint-disable-next-line no-await-in-loop
                              await addGuidance(line);
                            }
                          }} />
                        </td>
                      </tr>
                    ))}
                    {promptSuggestions.length === 0 && (
                      <tr><td colSpan={4} style={{ opacity: 0.5, textAlign: 'center' }}>No anomaly suggestions yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── LOGS ────────────────────────────────────────────────────────── */}
      {tab === 'logs' && (
        <div className="admin-section">
          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
            <input
              type="date"
              className="darkInput"
              value={logsDate}
              onChange={e => setLogsDate(e.target.value)}
              style={{ width: 150 }}
            />
            <select
              className="darkInput"
              value={logsLevel}
              onChange={e => setLogsLevel(e.target.value)}
              style={{ width: 130 }}
            >
              <option value="">All levels</option>
              <option value="debug">Debug</option>
              <option value="info">Info</option>
              <option value="warn">Warn</option>
              <option value="error">Error</option>
              <option value="exception">Exception</option>
            </select>
            <input
              className="darkInput"
              placeholder="Search message / user…"
              value={logsSearch}
              onChange={e => setLogsSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setLogsPage(0); loadLogs(logsDate, logsLevel, logsSearch, 0); } }}
              style={{ flex: 1, minWidth: 150 }}
            />
            <ActionButton label="Search" onClick={() => { setLogsPage(0); loadLogs(logsDate, logsLevel, logsSearch, 0); }} icon={<FontIcon iconName="Search" />} />
            <ActionButton label="Refresh" onClick={() => loadLogs(logsDate, logsLevel, logsSearch, logsPage)} icon={<FontIcon iconName="Refresh" />} />
          </div>

          {/* Summary */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 10, fontSize: '0.72rem', opacity: 0.6, alignItems: 'center' }}>
            <span>{logsTotal} log{logsTotal !== 1 ? 's' : ''} found</span>
            {logsTotal > LOGS_PAGE_SIZE && (
              <span>
                Page {logsPage + 1} of {Math.ceil(logsTotal / LOGS_PAGE_SIZE)}
              </span>
            )}
          </div>

          {logsLoading ? (
            <p style={{ textAlign: 'center', opacity: 0.5, fontSize: '0.82rem' }}>Loading logs…</p>
          ) : logs.length === 0 ? (
            <p style={{ textAlign: 'center', opacity: 0.5, fontSize: '0.82rem' }}>No logs found for this filter</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {logs.map((log: any) => {
                const isExpanded = logsExpanded === log.rowKey;
                const isError = log.level === 'exception' || log.level === 'error';
                const levelColors: Record<string, string> = {
                  debug: 'rgba(0,0,0,0.25)',
                  info: 'rgba(0,0,0,0.5)',
                  warn: '#cc9900',
                  error: '#cc4444',
                  exception: '#cc4444',
                };
                const time = (log.rowKey || '').split('_')[0] || log.timestamp_iso || '';
                const displayTime = time.length >= 19 ? time.slice(11, 19) : time;
                return (
                  <div
                    key={log.rowKey}
                    onClick={() => setLogsExpanded(isExpanded ? null : log.rowKey)}
                    style={{
                      background: isError ? 'rgba(204,68,68,0.08)' : 'rgba(0,0,0,0.06)',
                      borderRadius: 10,
                      padding: '8px 12px',
                      cursor: 'pointer',
                      transition: 'background 0.1s',
                      borderLeft: `3px solid ${levelColors[log.level] || 'rgba(0,0,0,0.1)'}`,
                    }}
                  >
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: '0.72rem', flexWrap: 'wrap' }}>
                      <span style={{
                        fontWeight: 700,
                        fontSize: '0.64rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em',
                        color: levelColors[log.level] || '#000',
                        minWidth: 55,
                      }}>{log.level}</span>
                      <span style={{ opacity: 0.4, fontVariantNumeric: 'tabular-nums', minWidth: 60 }}>{displayTime}</span>
                      <span style={{ fontWeight: 600, flex: 1 }}>{truncate(log.message || '', 80)}</span>
                      {log.userId && (
                        <span style={{
                          background: 'rgba(0,0,0,0.1)',
                          borderRadius: 6,
                          padding: '1px 7px',
                          fontSize: '0.64rem',
                          opacity: 0.6,
                          maxWidth: 120,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>{log.userId}</span>
                      )}
                      <FontIcon iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'} style={{ fontSize: '0.6rem', opacity: 0.3 }} />
                    </div>
                    {isExpanded && (
                      <div style={{ marginTop: 8, fontSize: '0.7rem', lineHeight: 1.5 }}>
                        {log.properties && (
                          <div style={{ marginBottom: 6 }}>
                            <span style={{ fontWeight: 700, opacity: 0.5, fontSize: '0.64rem', textTransform: 'uppercase' }}>Properties</span>
                            <pre style={{
                              margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                              background: 'rgba(0,0,0,0.06)', borderRadius: 8, padding: '8px 10px',
                              fontSize: '0.68rem', maxHeight: 200, overflowY: 'auto',
                            }}>{(() => {
                              try { return JSON.stringify(JSON.parse(log.properties), null, 2); } catch { return log.properties; }
                            })()}</pre>
                          </div>
                        )}
                        {log.error_message && (
                          <div style={{ marginBottom: 6 }}>
                            <span style={{ fontWeight: 700, opacity: 0.5, fontSize: '0.64rem', textTransform: 'uppercase', color: '#cc4444' }}>Error</span>
                            <div style={{ marginTop: 2, color: '#cc4444' }}>{log.error_message}</div>
                          </div>
                        )}
                        {log.error_stack && (
                          <pre style={{
                            margin: '4px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                            background: 'rgba(204,68,68,0.06)', borderRadius: 8, padding: '8px 10px',
                            fontSize: '0.64rem', maxHeight: 200, overflowY: 'auto', color: '#cc4444',
                          }}>{log.error_stack}</pre>
                        )}
                        <div style={{ marginTop: 6, opacity: 0.35, fontSize: '0.64rem' }}>
                          {log.partitionKey} / {log.rowKey}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {logsTotal > LOGS_PAGE_SIZE && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 14 }}>
              <button
                disabled={logsPage === 0}
                onClick={() => { const p = logsPage - 1; setLogsPage(p); loadLogs(logsDate, logsLevel, logsSearch, p); }}
                style={{
                  background: logsPage === 0 ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.12)',
                  color: '#000', border: 'none', borderRadius: 20, padding: '6px 16px',
                  cursor: logsPage === 0 ? 'default' : 'pointer', fontFamily: 'inherit', fontSize: '0.78rem',
                  opacity: logsPage === 0 ? 0.3 : 1,
                }}
              >← Prev</button>
              <span style={{ fontSize: '0.74rem', opacity: 0.5, alignSelf: 'center' }}>
                {logsPage + 1} / {Math.ceil(logsTotal / LOGS_PAGE_SIZE)}
              </span>
              <button
                disabled={(logsPage + 1) * LOGS_PAGE_SIZE >= logsTotal}
                onClick={() => { const p = logsPage + 1; setLogsPage(p); loadLogs(logsDate, logsLevel, logsSearch, p); }}
                style={{
                  background: (logsPage + 1) * LOGS_PAGE_SIZE >= logsTotal ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.12)',
                  color: '#000', border: 'none', borderRadius: 20, padding: '6px 16px',
                  cursor: (logsPage + 1) * LOGS_PAGE_SIZE >= logsTotal ? 'default' : 'pointer', fontFamily: 'inherit', fontSize: '0.78rem',
                  opacity: (logsPage + 1) * LOGS_PAGE_SIZE >= logsTotal ? 0.3 : 1,
                }}
              >Next →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
